import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoConfig from '@/data/seoConfig.json';
import { getInsightBySlug } from '@/data/staticInsights';

const DEFAULT_DESCRIPTION = seoConfig.organization.description;

function normalizePath(pathname) {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.replace(/\/+$/, '') || '/';
}

function absoluteUrl(path = '/') {
  if (/^https?:\/\//.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${seoConfig.baseUrl}${normalizedPath}`;
}

function canonicalUrlForPath(pathname) {
  const normalizedPath = normalizePath(pathname);
  return absoluteUrl(normalizedPath === '/' ? '/' : `${normalizedPath}/`);
}

function truncateDescription(text) {
  if (!text) {
    return DEFAULT_DESCRIPTION;
  }

  return text.length <= 158 ? text : `${text.slice(0, 155).trim()}...`;
}

function setMetaAttribute(attribute, key, content) {
  if (!content) {
    return;
  }

  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function setLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
}

function buildBreadcrumbSchema(pathname, title) {
  if (pathname === '/') {
    return null;
  }

  const segments = pathname.split('/').filter(Boolean);
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: seoConfig.baseUrl
    }
  ];

  segments.forEach((segment, index) => {
    const urlPath = `/${segments.slice(0, index + 1).join('/')}`;
    const isLast = index === segments.length - 1;

    items.push({
      '@type': 'ListItem',
      position: index + 2,
      name: isLast
        ? title.replace(/\s+\|\s+S Cube.*$/, '')
        : segment
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' '),
      item: canonicalUrlForPath(urlPath)
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items
  };
}

function buildOrganizationSchemas() {
  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: seoConfig.organization.name,
    legalName: seoConfig.organization.legalName,
    url: seoConfig.baseUrl,
    logo: absoluteUrl('/scube-logo.png'),
    email: seoConfig.organization.email,
    telephone: seoConfig.organization.telephone,
    description: seoConfig.organization.description,
    address: {
      '@type': 'PostalAddress',
      ...seoConfig.organization.address
    },
    areaServed: seoConfig.organization.areaServed
  };

  const professionalService = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: seoConfig.organization.name,
    url: seoConfig.baseUrl,
    image: absoluteUrl(seoConfig.defaultImage),
    email: seoConfig.organization.email,
    telephone: seoConfig.organization.telephone,
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      ...seoConfig.organization.address
    },
    areaServed: seoConfig.organization.areaServed,
    serviceType: seoConfig.organization.services
  };

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: seoConfig.siteName,
    url: seoConfig.baseUrl,
    publisher: {
      '@type': 'Organization',
      name: seoConfig.organization.name
    }
  };

  return [organization, professionalService, website];
}

function buildArticleSchema(insight, canonicalUrl) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: insight.title,
    description: truncateDescription(insight.excerpt),
    image: absoluteUrl(insight.image_url || seoConfig.defaultImage),
    datePublished: insight.created_at,
    dateModified: insight.created_at,
    author: {
      '@type': 'Organization',
      name: insight.author || seoConfig.organization.name
    },
    publisher: {
      '@type': 'Organization',
      name: seoConfig.organization.name,
      logo: {
        '@type': 'ImageObject',
        url: absoluteUrl('/scube-logo.png')
      }
    },
    mainEntityOfPage: canonicalUrl
  };
}

function getMetadataForPath(pathname) {
  const normalizedPath = normalizePath(pathname);
  const insightMatch = normalizedPath.match(/^\/insights\/([^/]+)$/);

  if (insightMatch) {
    const insight = getInsightBySlug(insightMatch[1]);

    if (insight) {
      return {
        title: `${insight.title} | S Cube AI Executive Guide`,
        description: truncateDescription(insight.excerpt),
        image: insight.image_url || seoConfig.defaultImage,
        pathname: normalizedPath,
        article: insight
      };
    }
  }

  const page = seoConfig.pages[normalizedPath] || seoConfig.pages['/'];

  return {
    ...page,
    pathname: normalizedPath
  };
}

function updateSchema(schemas) {
  let schemaTag = document.head.querySelector('#seo-schema');

  if (!schemaTag) {
    schemaTag = document.createElement('script');
    schemaTag.type = 'application/ld+json';
    schemaTag.id = 'seo-schema';
    document.head.appendChild(schemaTag);
  }

  schemaTag.textContent = JSON.stringify(schemas.filter(Boolean));
}

export default function Seo() {
  const location = useLocation();

  useEffect(() => {
    const metadata = getMetadataForPath(location.pathname);
    const canonicalUrl = canonicalUrlForPath(metadata.pathname);
    const imageUrl = absoluteUrl(metadata.image || seoConfig.defaultImage);
    const description = truncateDescription(metadata.description);

    document.title = metadata.title;
    document.documentElement.setAttribute('lang', 'en-IN');

    setMetaAttribute('name', 'description', description);
    setMetaAttribute('name', 'robots', 'index,follow');
    setMetaAttribute('property', 'og:site_name', seoConfig.siteName);
    setMetaAttribute('property', 'og:title', metadata.title);
    setMetaAttribute('property', 'og:description', description);
    setMetaAttribute('property', 'og:type', metadata.article ? 'article' : 'website');
    setMetaAttribute('property', 'og:url', canonicalUrl);
    setMetaAttribute('property', 'og:image', imageUrl);
    setMetaAttribute('name', 'twitter:card', 'summary_large_image');
    setMetaAttribute('name', 'twitter:title', metadata.title);
    setMetaAttribute('name', 'twitter:description', description);
    setMetaAttribute('name', 'twitter:image', imageUrl);
    setLink('canonical', canonicalUrl);

    const schemas = [
      ...buildOrganizationSchemas(),
      buildBreadcrumbSchema(metadata.pathname, metadata.title),
      metadata.article ? buildArticleSchema(metadata.article, canonicalUrl) : null
    ];

    updateSchema(schemas);
  }, [location.pathname]);

  return null;
}
